<script setup>
/**
 * FormControlMProductsProductsTypeId
 * Manages the primary product layout engine classification selection.
 * Handles dynamic rendering switching between plain-text states and select elements,
 * hiding adjacent pricing or inventory layers when dynamic variation option matrices exist.
 */
import {ref, computed, watch, onMounted} from 'vue'
import FormControlPlainText from "./FormControlPlainText.vue"
import api from '@/services/api'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)

// --- State Layers ---

const showMenu = ref(false)
const options = ref([])
const typesWithOptions = ref([])
const typeTitle = ref('')
const stockQuantityCache = ref(null)

// --- Synchronous Computeds ---

const originalData = computed(() => formStore.originalData || {})

const typeId = computed(() => props.currentData?.typeId)

const isTypeModified = computed(() => originalData.value.typeId !== typeId.value)

const typeHasOptions = computed(() => {
    if (!typesWithOptions.value || !typeId.value) return false
    return typesWithOptions.value.includes(typeId.value)
})

const showSaveNotice = computed(() => isTypeModified.value && typeHasOptions.value)

const showEditVariationsButton = computed(() => !isTypeModified.value && !!typeId.value && typeHasOptions.value)

// --- Methods ---

/**
 * Custom layout side-effect orchestrator.
 * Toggles adjacent pricing/inventory fields based on whether the product type has options,
 * and maintains a temporary local cache of stock balances to ensure smooth reverts.
 */
const toggleFields = () => {
    const fieldNames = [
        'trackStock', 'stockQuantity', 'stockLevel',
        'hideWhenSold', 'taxExempt', 'comparisonPrice',
        'sellingPrice', 'specialOffer', 'wasPrice'
    ]

    const formFields = formStore.fields || []

    formFields.forEach((field) => {
        if (fieldNames.includes(field.columnName)) {
            field.hidden = typeHasOptions.value
        }
    })

    // Handle stock tracking value cache swaps cleanly
    if (typeId.value) {
        stockQuantityCache.value = props.currentData.stockQuantity
        props.currentData.stockQuantity = null
    } else if (stockQuantityCache.value !== null) {
        props.currentData.stockQuantity = stockQuantityCache.value
    }
}

/**
 * Maps raw IDs against preloaded store information to generate select picklists.
 */
const setOptions = async (typeIds) => {
    try {
        const objs = await itemDataStore.preload({
            tableName: 'product_types',
            ids: typeIds,
        })

        const menuOptions = [{value: 0, text: '- none -'}]

        objs.forEach((obj) => {
            menuOptions.push({value: obj.id, text: obj.title})
        })

        options.value = menuOptions.sort((a, b) => a.text.localeCompare(b.text))
    } catch (err) {
        console.error('Failed to populate type picklist settings options:', err)
    }
}

/**
 * Queries the catalog structure asynchronously to update the text display title.
 */
const fetchTypeTitle = async () => {
    if (!typeId.value) {
        typeTitle.value = ''
        return
    }

    try {
        const obj = await itemDataStore.get({
            tableName: 'product_types',
            id: typeId.value
        })
        typeTitle.value = obj?.title || ''
    } catch (err) {
        console.error('Failed to resolve layout type text header value:', err)
        typeTitle.value = ''
    }
}

/**
 * Resolves the underlying category definition rules out of the database structure.
 */
const fetchTypesWithOptions = async () => {
    try {
        const response = await api.get('api/component/product_options/productTypeId', {
            params: {sort: 'productTypeId'}
        })
        typesWithOptions.value = response?.data?.values || []
    } catch (err) {
        console.error('Failed to resolve options constraint definitions:', err)
    }
}

/**
 * Redirects the administrative session context out to the associated variant layout grid.
 */
const editVariations = async () => {
    try {
        const response = await api.get('api/Products/getTypesTableNames')
        const tableName = response?.data?.[typeId.value]
        if (tableName) {
            window.location = `/#/${tableName}?productId=${props.currentData.id}`
        }
    } catch (err) {
        console.error('Failed navigating to variations control system panel:', err)
    }
}

// --- Watchers & Initializer Lifecycle ---

// FIX: Runs side-effect layout mutations safely via an explicit structural watcher instead of inside a computed property
watch([typeId, typeHasOptions], () => {
    toggleFields()
    fetchTypeTitle()
}, {immediate: false})

onMounted(async () => {
    // Fire structural reference definitions in parallel
    await fetchTypesWithOptions()
    await fetchTypeTitle()

    try {
        // Load all product types and populate the select menu. If none exist yet then remove the select menu.
        const typeIdsResponse = await api.get('api/component/product_types/id', {
            params: {__isPublished: true}
        })

        const typeIds = typeIdsResponse?.data?.values
        if (typeIds && typeIds.length) {
            await setOptions(typeIds)
        } else {
            props.field.hidden = true
            return
        }

        if (!props.currentData.id) {
            await formStore.setDefaultValue({
                name: props.field.name,
                value: 0
            })
            showMenu.value = true
            return
        }

        if (!typeId.value) {
            showMenu.value = true
            return
        }

        // Check if variations exist to lock/unlock type mutations safety valves
        const tablesResponse = await api.get('api/Products/getTypesTableNames')

        const targetTableName = tablesResponse?.data?.[typeId.value]
        if (targetTableName) {
            const variationsResponse = await api.get(`api/component/${targetTableName}/id`, {
                params: {productId: props.currentData.id}
            })

            const variationIds = variationsResponse?.data?.values || []

            // If the product has NO variations, allow editing the menu dropdown
            if (!variationIds.length) {
                showMenu.value = true
            }
        }
    } catch (err) {
        console.error('Critical crash during category picker instantiation rules routine:', err)
    }
})
</script>

<template>
    <div class="form-control-products-type-id-container">

        <FormControlPlainText
            v-if="typeTitle && !showMenu"
            :value="typeTitle"
            :form-id="formId"
        />

        <select
            v-if="showMenu"
            v-model="currentData[field.name]"
            class="form-select mb-2"
        >
            <option v-for="option in options" :key="option.value" :value="option.value">
                {{ option.text }}
            </option>
        </select>

        <button
            v-if="currentData.typeId && !showSaveNotice"
            :disabled="!showEditVariationsButton"
            class="btn btn-light btn-sm"
            type="button"
            @click="editVariations"
        >
            Edit variations
        </button>

        <small v-if="showSaveNotice" class="text-warning ms-2">
            <em>Save required</em>
        </small>

    </div>
</template>